// Atualização OTIMISTA do operador: tocar "Fazendo"/"Feito" muda a tela na hora; se a
// chamada falhar (sinal fraco), a tela volta ao que era e a pessoa vê o motivo.
// Funções puras — o repo não tem jsdom, então a regra mora aqui, não no componente.

interface ItemCom {
  id: string;
  status: string;
  concluidoEm: string | null;
  desfazivelPorMim?: boolean;
  secao?: string;
  dataPrevista?: string | null;
}

export interface ListaCache<I extends ItemCom = ItemCom> {
  grupos: Array<{ itens: I[]; feitos: I[] } & Record<string, unknown>>;
}

export interface MinhasCache<I extends ItemCom = ItemCom> {
  itens: I[];
  feitosHoje: I[];
  semVinculo: boolean;
}

/** Em andamento primeiro, o resto na ordem em que estava (sort estável). */
function emAndamentoPrimeiro<I extends ItemCom>(itens: I[]): I[] {
  const fazendo = itens.filter(i => i.status === "em_andamento");
  const resto = itens.filter(i => i.status !== "em_andamento");
  return fazendo.concat(resto);
}

const fazendo = <I extends ItemCom>(i: I): I => ({ ...i, status: "em_andamento", secao: i.secao ? "fazendo" : i.secao });
const feito = <I extends ItemCom>(i: I, agoraIso: string): I => ({
  ...i, status: "concluida", concluidoEm: agoraIso, desfazivelPorMim: true,
});

// ─── Lista (por frente) ───────────────────────────────────────────────────────

export function fazendoNaLista<L extends ListaCache>(d: L, id: string): L {
  return {
    ...d,
    grupos: d.grupos.map(g => (g.itens.some(i => i.id === id)
      ? { ...g, itens: emAndamentoPrimeiro(g.itens.map(i => (i.id === id ? fazendo(i) : i))) }
      : g)),
  };
}

export function feitoNaLista<L extends ListaCache>(d: L, id: string, agoraIso: string): L {
  return {
    ...d,
    grupos: d.grupos.map(g => {
      const alvo = g.itens.find(i => i.id === id);
      if (!alvo) return g;
      return { ...g, itens: g.itens.filter(i => i.id !== id), feitos: [feito(alvo, agoraIso)].concat(g.feitos) };
    }),
  };
}

// ─── Minhas ───────────────────────────────────────────────────────────────────

export function fazendoEmMinhas<M extends MinhasCache>(d: M, id: string): M {
  return { ...d, itens: emAndamentoPrimeiro(d.itens.map(i => (i.id === id ? fazendo(i) : i))) };
}

export function feitoEmMinhas<M extends MinhasCache>(d: M, id: string, agoraIso: string): M {
  const alvo = d.itens.find(i => i.id === id);
  if (!alvo) return d;
  return { ...d, itens: d.itens.filter(i => i.id !== id), feitosHoje: [feito(alvo, agoraIso)].concat(d.feitosHoje) };
}

// ─── Orquestração: aplicar → chamar → (reverter | aceitar) ─────────────────────

export type ResultadoOtimista =
  | { ok: true; recuperado: boolean }
  | { ok: false; erro: unknown };

/**
 * Aplica a mudança na tela, chama o servidor e:
 *  - sucesso → mantém;
 *  - 409 cujo estado já é o pedido (`jaNoEstado`) → também é sucesso (alguém já fez, ou
 *    a resposta anterior se perdeu): mantém e avisa `recuperado`;
 *  - qualquer outra falha → REVERTE a tela e devolve o erro.
 */
export async function executarOtimista<T>(args: {
  aplicar: () => void;
  reverter: () => void;
  chamar: () => Promise<T>;
  jaNoEstado?: (erro: unknown) => boolean;
}): Promise<ResultadoOtimista> {
  args.aplicar();
  try {
    await args.chamar();
    return { ok: true, recuperado: false };
  } catch (erro) {
    if (args.jaNoEstado && args.jaNoEstado(erro)) return { ok: true, recuperado: true };
    args.reverter();
    return { ok: false, erro };
  }
}
